/**
 * Research Library Admin Dashboard
 * Manage research library health, coverage, and batch operations
 *
 * Phase 3+ Implementation (November 2025)
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Package,
  Zap,
  BarChart3,
  RefreshCw,
  Loader2,
  ArrowRight,
} from 'lucide-react';

import {
  getLibraryHealth,
  getLibraryCoverage,
  getPriorityConfig,
  fillPriorityGaps,
  triggerBatchResearch,
} from '../lib/api/domainPacksApi';
import { Card, CardHeader, CardBody } from '../components/ui/Card';
import Button from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';

export const ResearchLibraryAdmin: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedGapCount, setSelectedGapCount] = useState(10);

  // Fetch library health
  const { data: health, isLoading: healthLoading, error: healthError } = useQuery({
    queryKey: ['library-health'],
    queryFn: getLibraryHealth,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch library coverage
  const { data: coverage, isLoading: coverageLoading, error: coverageError } = useQuery({
    queryKey: ['library-coverage'],
    queryFn: getLibraryCoverage,
  });

  // Fetch priority configuration
  const { data: priorityConfig } = useQuery({
    queryKey: ['priority-config'],
    queryFn: getPriorityConfig,
  });

  // Fill gaps mutation
  const fillGapsMutation = useMutation({
    mutationFn: (count: number) => fillPriorityGaps(count, 'admin'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['library-health'] });
      queryClient.invalidateQueries({ queryKey: ['library-coverage'] });
    },
  });

  // Batch research mutation
  const batchResearchMutation = useMutation({
    mutationFn: (requests: any[]) => triggerBatchResearch(requests, 'admin'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['library-health'] });
      queryClient.invalidateQueries({ queryKey: ['library-coverage'] });
    },
  });

  const getHealthColor = (score: number): string => {
    if (score >= 80) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getHealthBgColor = (score: number): string => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 50) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  if (healthLoading || coverageLoading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary-500 mx-auto mb-4" />
          <p className="text-neutral-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (healthError || coverageError) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <Card className="border-red-500 max-w-md">
          <CardBody className="py-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">Failed to Load Dashboard</h3>
            <p className="text-neutral-600">
              {healthError instanceof Error ? healthError.message : coverageError instanceof Error ? coverageError.message : 'An error occurred'}
            </p>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Activity className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-neutral-900">Research Library Admin</h1>
              <p className="text-neutral-600">Monitor health, coverage, and manage research gaps</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Health Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Overall Health */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Overall Health
              </h3>
            </CardHeader>
            <CardBody>
              <div className="flex items-center justify-between">
                <div className={`text-5xl font-bold ${getHealthColor(health?.health_score ?? 0)}`}>
                  {health?.health_score ?? 0}
                </div>
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${getHealthBgColor(health?.health_score ?? 0)}`}>
                  {health && (health.health_score ?? 0) >= 80 ? (
                    <CheckCircle2 className="h-8 w-8 text-green-600" />
                  ) : (
                    <AlertCircle className="h-8 w-8 text-red-600" />
                  )}
                </div>
              </div>
              <p className="text-sm text-neutral-600 mt-4">
                {(health?.health_score ?? 0) >= 80
                  ? 'Excellent library health'
                  : (health?.health_score ?? 0) >= 50
                  ? 'Needs improvement'
                  : 'Critical - immediate action required'}
              </p>
            </CardBody>
          </Card>

          {/* Coverage Score */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
                <Package className="h-5 w-5" />
                Coverage
              </h3>
            </CardHeader>
            <CardBody>
              <div className="text-5xl font-bold text-primary-600">
                {health?.coverage_score?.toFixed(1) || 0}%
              </div>
              <p className="text-sm text-neutral-600 mt-4">
                {health?.total_coverage || '0/0'} domain-industry combinations covered
              </p>
              <div className="w-full bg-neutral-200 rounded-full h-2 mt-3">
                <div
                  className="bg-primary-500 h-2 rounded-full transition-all"
                  style={{ width: `${health?.coverage_score || 0}%` }}
                />
              </div>
            </CardBody>
          </Card>

          {/* Quality Score */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Quality
              </h3>
            </CardHeader>
            <CardBody>
              <div className="text-5xl font-bold text-secondary-600">
                {health?.quality_score?.toFixed(1) || 0}%
              </div>
              <p className="text-sm text-neutral-600 mt-4">
                Average quality across all research
              </p>
              <div className="w-full bg-neutral-200 rounded-full h-2 mt-3">
                <div
                  className="bg-secondary-500 h-2 rounded-full transition-all"
                  style={{ width: `${health?.quality_score || 0}%` }}
                />
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Recommendations */}
        {health && health.recommendations && health.recommendations.length > 0 && (
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Recommended Actions
              </h3>
            </CardHeader>
            <CardBody>
              <ul className="space-y-3">
                {health.recommendations.map((rec: string, idx: number) => (
                  <li key={idx} className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-primary-500 flex-shrink-0 mt-0.5" />
                    <span className="text-neutral-700">{rec}</span>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        )}

        {/* Industry Coverage */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Industry Coverage ({coverage?.industries?.length || 0} industries)
            </h3>
          </CardHeader>
          <CardBody className="space-y-4">
            {coverage?.industries?.map((industry: any) => (
              <div key={industry.industry}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-neutral-900 w-48">{industry.industry_display}</span>
                    <Badge variant={industry.coverage_percentage >= 80 ? 'success' : industry.coverage_percentage >= 50 ? 'warning' : 'error'}>
                      {industry.covered_domains}/{industry.total_domains} domains
                    </Badge>
                  </div>
                  <span className="text-sm font-semibold text-neutral-700">
                    {industry.coverage_percentage.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-neutral-200 rounded-full h-4">
                  <div
                    className={`h-4 rounded-full transition-all ${
                      industry.coverage_percentage >= 80
                        ? 'bg-green-500'
                        : industry.coverage_percentage >= 50
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                    }`}
                    style={{ width: `${industry.coverage_percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </CardBody>
        </Card>

        {/* Top Gaps */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Top Priority Gaps ({coverage?.top_gaps?.length || 0} identified)
            </h3>
          </CardHeader>
          <CardBody>
            {coverage?.top_gaps && coverage.top_gaps.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-neutral-200">
                    <tr>
                      <th className="text-left py-3 px-2 text-sm font-semibold text-neutral-700">Domain</th>
                      <th className="text-left py-3 px-2 text-sm font-semibold text-neutral-700">Industry</th>
                      <th className="text-left py-3 px-2 text-sm font-semibold text-neutral-700">Priority</th>
                      <th className="text-left py-3 px-2 text-sm font-semibold text-neutral-700">Est. Time</th>
                      <th className="text-left py-3 px-2 text-sm font-semibold text-neutral-700">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coverage.top_gaps.slice(0, 20).map((gap: any, idx: number) => (
                      <tr key={idx} className="border-b border-neutral-100 hover:bg-neutral-50">
                        <td className="py-3 px-2 text-sm text-neutral-900 font-medium">{gap.domain_name}</td>
                        <td className="py-3 px-2 text-sm text-neutral-700">{gap.industry_display}</td>
                        <td className="py-3 px-2">
                          <Badge variant={gap.priority_score > 0.8 ? 'error' : gap.priority_score > 0.5 ? 'warning' : 'neutral'} size="sm">
                            {(gap.priority_score * 100).toFixed(0)}
                          </Badge>
                        </td>
                        <td className="py-3 px-2 text-sm text-neutral-600">{gap.estimated_time}</td>
                        <td className="py-3 px-2 text-sm text-neutral-600">{gap.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-neutral-600 py-4 text-center">No gaps identified - library is complete!</p>
            )}
          </CardBody>
        </Card>

        {/* Actions */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Batch Actions
            </h3>
          </CardHeader>
          <CardBody>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Select number of gaps to fill:
                </label>
                <div className="flex gap-3">
                  {[5, 10, 20, 50].map((count) => (
                    <button
                      key={count}
                      onClick={() => setSelectedGapCount(count)}
                      className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                        selectedGapCount === count
                          ? 'border-primary-500 bg-primary-50 text-primary-700 font-semibold'
                          : 'border-neutral-200 text-neutral-700 hover:border-neutral-300'
                      }`}
                    >
                      Top {count}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => fillGapsMutation.mutate(selectedGapCount)}
                  disabled={fillGapsMutation.isPending || !coverage?.top_gaps || coverage.top_gaps.length === 0}
                >
                  {fillGapsMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Starting Research...
                    </>
                  ) : (
                    <>
                      <Zap className="h-4 w-4 mr-2" />
                      Fill Top {selectedGapCount} Gaps
                    </>
                  )}
                </Button>

                <Button
                  variant="secondary"
                  onClick={() => {
                    queryClient.invalidateQueries({ queryKey: ['library-health'] });
                    queryClient.invalidateQueries({ queryKey: ['library-coverage'] });
                  }}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh Data
                </Button>
              </div>

              {fillGapsMutation.isSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="h-5 w-5" />
                    <span className="font-medium">Gap filling started successfully!</span>
                  </div>
                  <p className="text-sm text-green-600 mt-2">
                    Research jobs have been queued for {selectedGapCount} domain-industry combinations.
                  </p>
                </div>
              )}

              {fillGapsMutation.isError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-medium">Failed to start gap filling</span>
                  </div>
                  <p className="text-sm text-red-600 mt-2">
                    {fillGapsMutation.error instanceof Error ? fillGapsMutation.error.message : 'An error occurred'}
                  </p>
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Priority Configuration (Optional - can be expanded) */}
        {priorityConfig && (
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold text-neutral-900">Priority Configuration</h3>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-semibold text-neutral-700 mb-2">Priority Domains ({priorityConfig.priority_domains?.length || 0})</h4>
                  <div className="flex flex-wrap gap-2">
                    {priorityConfig.priority_domains?.map((domain: string) => (
                      <Badge key={domain} variant="info" size="sm">{domain}</Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-neutral-700 mb-2">Priority Industries ({priorityConfig.priority_industries?.length || 0})</h4>
                  <div className="flex flex-wrap gap-2">
                    {priorityConfig.priority_industries?.map((industry: string) => (
                      <Badge key={industry} variant="secondary" size="sm">{industry}</Badge>
                    ))}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ResearchLibraryAdmin;
